<template>
  <div>
    <v-row class="margin-b-6">
      <v-col cols="12" class="padding-b-0">
        <h3>Search</h3>
      </v-col>
      <v-col cols="6" md="3" class="padding-b-0">
        <label>Quote ID</label>
        <v-text-field
          id="my-profile-text-first-name"
          ref="firstName"
          v-model="search.quoteId"
          required
          outlined
          placeholder="Ex. 500001"
          hide-details
          tabindex="1"
        />
      </v-col>
      <v-col cols="6" md="3" class="padding-b-0">
        <label>Customer Name</label>
        <v-text-field
          id="my-profile-text-last-name"
          ref="lastName"
          v-model="search.customerName"
          required
          outlined
          hide-details
          placeholder="Ex. John Doe"
          tabindex="2"
        />
      </v-col>
      <v-col cols="6" md="3" class="padding-b-0">
        <label>Email</label>
        <v-text-field
          id="my-profile-text-email"
          ref="email"
          v-model="search.email"
          required
          outlined
          hide-details
          placeholder="Ex. john.doe@email.com"
          tabindex="3"
        />
      </v-col>
      <v-col cols="6" md="3" class="padding-b-0">
        <label>Checkout Date</label>
        <v-menu
          ref="menu"
          v-model="menu"
          :return-value.sync="search.convertedOn"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              id="finance-checkout-data"
              ref="checkoutDate"
              v-model="search.convertedOn"
              required
              readonly
              outlined
              hide-details
              placeholder="Ex. 2021-10-20"
              tabindex="3"
              :append-icon="search.convertedOn ? 'close' : 'calendar_today'"
              v-bind="attrs"
              @click:append="handleAppendIconClick"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="search.convertedOn"
            no-title
            scrollable
            @input="$refs.menu.save(search.convertedOn)"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-data-table
      v-if="userSearchExists"
      :headers="headers"
      :items="quotes"
      :options.sync="tableOptions"
      :server-items-length="quoteCount"
      :loading="loading"
      loading-text="Loading records..."
      dense
      @update:options="getQuotes"
      @click:row="handleClickRow"
    >
      <template #item.convertedOn="{ item }">
        <span>{{ formatDate(item.convertedOn) }}</span>
      </template>
      <template #item.customerName="{ item }">
        <span>
          {{ `${item.customer.firstName} ${item.customer.lastName}` }}
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Watch } from 'vue-property-decorator'
import { filter } from '@/utils/filter'
import quotes from '@/services/quotes'
import { DateTime } from 'luxon'
import { logger } from '@/utils/logger'
import { FinanceTableViewQuote } from '@/models/dto'
import { DataOptions, DataTableHeader } from 'vuetify'

@Component({})
export default class FinanceChargeDispute extends Vue {
  @Watch('search', { deep: true })
  searchChanged() {
    if (this.debounce) {
      window.clearTimeout(this.debounce)
    }
    this.debounce = window.setTimeout(async () => {
      await this.getQuotes()
    }, 250)
  }

  search = {
    quoteId: null,
    customerName: null,
    email: null,
    convertedOn: null,
  }
  datePickerMenu = false
  headers: DataTableHeader[] = [
    { text: 'Quote ID', value: 'quoteId', sortable: false },
    { text: 'Name', value: 'customerName', sortable: false },
    { text: 'Email', value: 'customer.email', sortable: false },
    { text: 'Checkout Date', value: 'convertedOn', sortable: false },
  ]
  quotes: FinanceTableViewQuote[] = []
  quoteCount = 0
  tableOptions: DataOptions = {
    page: 1,
    itemsPerPage: 10,
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
    sortBy: [],
    sortDesc: [],
  }
  debounce = null
  loading = false
  menu = false

  get paginationPages(): number {
    return Math.ceil(this.quoteCount / this.tableOptions.itemsPerPage)
  }
  get userSearchExists(): boolean {
    // eslint-disable-next-line no-unused-vars
    for (const [key, value] of Object.entries(this.search)) {
      if (value) {
        return true
      }
    }
    return false
  }

  async getQuotes(): Promise<void> {
    const params = this.buildQuotesParams()
    this.loading = true
    const { data } = await quotes.financeAdminTableView(params)
    this.quoteCount = data.count
    this.quotes = data.resultList
    this.loading = false
  }
  buildQuotesParams() {
    const filterIsConverted = {
      column: { _t_id: '198b3123', value: 'isConverted', filterType: 'eq' },
      value: true,
    }
    const quotesDataFilter = filter()
    const filterParentAnd = quotesDataFilter.createParent('and')
    quotesDataFilter.add(filterParentAnd, filterIsConverted)

    const filterIsMarketplace = {
      column: {
        _t_id: '41a65a1b',
        value: 'isCharterUpQuote',
        filterType: 'eq',
      },
      value: true,
    }
    quotesDataFilter.add(filterParentAnd, filterIsMarketplace)

    if (this.search.quoteId) {
      const filterQuoteId = {
        column: { _t_id: 'f3523e1d', value: 'quoteId', filterType: 'eq' },
        value: this.search.quoteId,
      }
      quotesDataFilter.add(filterParentAnd, filterQuoteId)
    }
    if (this.search.customerName) {
      let firstName
      let lastName
      const spaceIndex = this.search.customerName.indexOf(' ')
      if (spaceIndex) {
        firstName = this.search.customerName.substr(
          0,
          this.search.customerName.indexOf(' ')
        )
        lastName = this.search.customerName.substr(
          this.search.customerName.indexOf(' ') + 1
        )
      } else {
        firstName = this.search.customerName
        lastName = this.search.customerName
      }
      const filterCustomerFirstName = {
        column: {
          _t_id: '907abb4e',
          value: 'customer.firstName',
          filterType: 'contains',
        },
        value: firstName,
      }
      const filterCustomerLastName = {
        column: {
          _t_id: '22c508dc',
          value: 'customer.lastName',
          filterType: 'contains',
        },
        value: lastName,
      }
      quotesDataFilter.add(filterParentAnd, filterCustomerFirstName)
      quotesDataFilter.add(filterParentAnd, filterCustomerLastName)
    }
    if (this.search.email) {
      const filterCustomerEmail = {
        column: {
          _t_id: '62ad758d',
          value: 'customer.email',
          filterType: 'contains',
        },
        value: this.search.email,
      }
      quotesDataFilter.add(filterParentAnd, filterCustomerEmail)
    }
    if (this.search.convertedOn) {
      const filterConvertedOnGTE = {
        column: {
          _t_id: 'f3472fe8',
          value: 'convertedOn',
          filterType: 'gte',
        },
        value: this.search.convertedOn,
      }
      const filterConvertedOnLTE = {
        column: {
          _t_id: 'd32c8bac',
          value: 'convertedOn',
          filterType: 'lte',
        },
        value: this.search.convertedOn,
      }
      quotesDataFilter.add(filterParentAnd, filterConvertedOnGTE)
      quotesDataFilter.add(filterParentAnd, filterConvertedOnLTE)
    }
    const params = {
      pageSize: this.tableOptions.itemsPerPage,
      page: this.tableOptions.page,
      filters: quotesDataFilter.asQueryParams(),
    }
    return params
  }
  formatDate(timestamp: string): string {
    const datetime = DateTime.fromISO(timestamp)
    return datetime.toFormat('yyyy-LL-dd - hh:mm a')
  }
  async handleClickRow(row: FinanceTableViewQuote): Promise<void> {
    const tripId = row?.trips?.[0]?.tripId
    if (!tripId) {
      console.warn('No trips associated with this quote')
      return
    }
    try {
      const data = await quotes.convertedByCompany(tripId)
      if (data.data.successful) {
        const convertedCompanyId = data?.data?.companyId
        this.$router.push({
          name: 'checkout',
          params: {
            quoteId: row.quoteId.toString(),
            providerId: convertedCompanyId.toString(),
            disputedCheckout: 'true',
          },
        })
      } else {
        logger.error(data.data.message)
      }
    } catch (e) {
      logger.error(e)
    }
  }
  handleAppendIconClick(): void {
    this.search.convertedOn = null
  }
}
</script>

<style lang="scss" scoped>
@import '~@/scss/colors';

.v-text-field {
  ::v-deep .v-input__append-inner {
    z-index: 1;
    font-size: 1.375rem;
    position: absolute;
    top: 7px;
    right: 12px;
    i.v-icon {
      color: $gray-light !important;
    }
  }
  &.v-input--is-focused {
    ::v-deep .v-input__append-inner {
      i.v-icon {
        color: $primary !important;
      }
    }
  }
}

::v-deep .v-data-table {
  table {
    tbody {
      tr {
        cursor: pointer !important;
      }
    }
  }
}
</style>
